import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { ReactComponent as SortIcon } from 'assets/icons/List.svg';
import { clsx } from 'clsx';

import type { IDraggableItemProps, IDragItem } from './interfaces/IDraggableList';

import styles from './scss/DraggableListItem.module.scss';

function DraggableListItem({
  index,
  onMove,
  children,
  className = '',
  itemType = 'DRAGGABLE_ITEM',
  showNumber = false,
}: IDraggableItemProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop<IDragItem, void, {}>({
    accept: itemType,
    hover(dragItem: IDragItem) {
      if (!ref.current || dragItem.index === index) {
        return;
      }

      onMove(dragItem.index, index);
      // eslint-disable-next-line no-param-reassign
      dragItem.index = index;
    },
  });

  const [{ isDragging }, drag] = useDrag<IDragItem, void, { isDragging: boolean }>({
    type: itemType,
    item: { index, type: itemType },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={clsx(styles.draggableItem, className, {
        [styles.isDragging]: isDragging,
      })}
    >
      {showNumber ? (
        <div className={styles.itemNumber}>
          {index + 1}
        </div>
      ) : null}
      <div className={styles.dragHandle}>
        <SortIcon />
      </div>
      <div className={styles.content}>
        {children}
      </div>
    </div>
  );
}

export default DraggableListItem;
